import { Trans } from "@lingui/react/macro";
import { useDraggable } from "@dnd-kit/core";
import dayjs from "dayjs";
import React from "react";
import { BsCurrencyEuro } from "react-icons/bs";
import styled from "styled-components";
import { useCalendarStore } from "../../../store/useCalendarStore";
import { getStatusIcon } from "../../../utils";
import useDrag from "../../hooks/useDrag";
import {
  returnHeightOfEventBlock,
  returnYoffsetForEvent,
} from "@/utils/appointment-utils";
import { HoverContent } from "./HoverContent";
import { MarketplaceBookingIndicator } from "@/Components/NextBase/Appointment/MarketplaceBookingIndicator";
import { FiGlobe } from "react-icons/fi";
import { i18n } from "@lingui/core";
import { Heart, HeartPlus, CircleAlert, ReceiptText } from "lucide-react";
import { Tooltip } from "@heroui/react";
import { cn } from "@/utils";
import { useShallow } from "zustand/react/shallow";
import { useDebouncedValue } from "@mantine/hooks";
import { useClientFeature } from "@/hooks/useClientFeature";
import { useStaffRequestIndicatorVisible } from "@/hooks/useStaffRequestIndicator";

function getInitials(name) {
  if (!name) return "";
  const cleanName = name.replace(/[^a-zA-Z0-9\s]/g, "");
  const words = cleanName.trim().split(/\s+/);
  return words
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase() ?? "")
    .join("");
}

const AppointmentBlock = ({
  appointment,
  resource,
  user = null,
  usersInOrder = null,
  resourcesInOrder,
  columnDate,
}) => {
  const {
    appointmentId,
    formattedData,
    serviceColor,
    customers,
    serviceId,
    isPreScheduled,
    overlapIndex,
    permission,
    managePermission,
    numberOfNextResources,
    numberOfNextUsers,
    blockBeforeMins,
    attendance,
    isTimeoffBlockingResource,
    hoverDate: appointmentDate,
  } = appointment;

  // SingleUserAppointmentContent is invoked as a plain function inside a
  // branch, so a hook called in there would drop out of the render whenever a
  // drag swaps in the overlay content. It has to be read here.
  const isStaffRequestIndicatorVisible = useStaffRequestIndicatorVisible();

  const {
    calendarView,
    setSelectedAppointmentId,
    viewMode,
    setHideModalTabs,
    services,
    users,
    resources,
    calendarMode,
    setLastClickYOffset,
    lastClickYOffset,
  } = useCalendarStore(
    useShallow((state) => ({
      calendarView: state.calendarView,
      setSelectedAppointmentId: state.setSelectedAppointmentId,
      viewMode: state.viewMode,
      setHideModalTabs: state.setHideModalTabs,
      services: state.services,
      users: state.users,
      resources: state.resources,
      calendarMode: state.calendarMode,
      setLastClickYOffset: state.setLastClickYOffset,
      lastClickYOffset: state.lastClickYOffset,
    })),
  );

  const [popoverOpen, setPopoverOpen] = React.useState(false);

  const { isFeatureEnabled } = useClientFeature();
  const hideIsReceiptIssuedIconOnAppointment = isFeatureEnabled(
    "hideIsReceiptIssuedIconOnAppointment",
  );

  const { startTime, endTime } = formattedData;

  const service = useCalendarStore((state) =>
    state.services.find((service) => service.serviceId === serviceId),
  );

  const { calendarData, draggingId } = useCalendarStore(
    useShallow((state) => ({
      calendarData: state.calendarData,
      draggingId: state.draggingId,
    })),
  );

  const { calculateTimeForDragYOffset } = useDrag();

  const isShown = appointment.filterEnabled;

  const locationStart = dayjs(`${columnDate}T${calendarData.startTime}`);
  const locationEnd = dayjs(`${columnDate}T${calendarData.endTime}`);
  const appointmentStart = dayjs(`${appointment.date}T${startTime}`);

  const isAppointmentOverMultipleDays =
    appointmentStart
      .add(appointment.duration, "minutes")
      .format("YYYY-MM-DD") !== appointmentStart.format("YYYY-MM-DD");

  const appointmentStartInDay = (() => {
    if (dayjs(`${appointment.date}T${startTime}`).isBefore(locationStart)) {
      return locationStart;
    }

    return dayjs(`${appointment.date}T${startTime}`);
  })();

  const yOffset = returnYoffsetForEvent({
    calendarStartTime: calendarData.startTime,
    startTimeEvent: appointmentStartInDay,
    timeStep: calendarData.timeStep,
    slotHeight: calendarData.slotHeight,
  });

  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: `draggable-${appointmentId}-${user || resource}-${columnDate}`,
    data: {
      appointment,
      currentResourceId: resource,
      currentResources: resourcesInOrder?.slice(
        resourcesInOrder.indexOf(resource),
        resourcesInOrder.indexOf(resource) + numberOfNextResources,
      ),
      currentUsers: usersInOrder?.slice(
        usersInOrder.indexOf(user),
        usersInOrder.indexOf(user) + numberOfNextUsers,
      ),
      currentUserId: user,
      isOverMultipleResources: numberOfNextResources > 1,
      isOverMultipleUsers: numberOfNextUsers > 1,
      yOffset,
    },
    disabled: !managePermission,
  });

  const [wasDragging] = useDebouncedValue(transform != null, 50);

  const dragTime = calculateTimeForDragYOffset(
    yOffset + transform?.y + lastClickYOffset,
  );

  // Update the appointment's current drag time whenever it changes
  React.useEffect(() => {
    if (transform) {
      appointment.currentDragTime = dragTime;
    } else {
      if (popoverOpen) {
        setPopoverOpen(false);
      }
    }
  }, [dragTime, transform]);

  if (draggingId === appointmentId && !transform) {
    return null;
  }

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y + lastClickYOffset}px, 0)`,
        zIndex: 99999,
      }
    : undefined;

  const MAX_TOTAL_WIDTH = 90;
  const {
    width: initialWidth,
    xOffset: initialXOffset,
    zIndex: initialZIndex,
  } = (() => {
    // Use durationZIndex for z-ordering: shorter appointments always on top
    const zIndex = appointment.durationZIndex ?? 100;

    if (appointment.overlapCount === 1) {
      return {
        width: MAX_TOTAL_WIDTH,
        xOffset: 0,
        zIndex,
      };
    } else {
      const xOffsetConst = 90 / appointment.overlapCount;

      return {
        width: MAX_TOTAL_WIDTH - overlapIndex * xOffsetConst,
        xOffset: appointment.overlapIndex * xOffsetConst,
        zIndex,
      };
    }
  })();

  const width = (() => {
    if (numberOfNextResources > 1) {
      return 100 - initialXOffset + (numberOfNextResources - 1) * 100;
    }
    if (numberOfNextUsers > 1) {
      return 100 - initialXOffset + (numberOfNextUsers - 1) * 100;
    }
    return initialWidth;
  })();

  const xOffset = initialXOffset;
  const zIndex = initialZIndex;

  const spanCount =
    numberOfNextResources > 1
      ? numberOfNextResources
      : numberOfNextUsers > 1
        ? numberOfNextUsers
        : 1;

  const hoverWidth = spanCount * 100;

  const serviceName =
    services.find((s) => s.serviceId === serviceId)?.name || "";

  if (serviceName) {
    appointment.serviceName = serviceName;
  }

  const onClickAppointment = (e) => {
    e.stopPropagation();

    if (!permission) {
      return;
    }

    setHideModalTabs(true);
    setSelectedAppointmentId(appointmentId);
    setPopoverOpen(false);
  };

  const appointmentEndInDay = (() => {
    if (
      dayjs(`${appointment.date}T${startTime}`)
        .add(appointment.duration, "minutes")
        .isAfter(locationEnd)
    ) {
      return locationEnd;
    }

    return dayjs(`${appointment.date}T${startTime}`).add(
      appointment.duration,
      "minutes",
    );
  })();

  const calculatedDuration = Math.max(
    appointmentEndInDay.diff(appointmentStartInDay, "minutes"),
    0,
  );

  const startDuration = Math.max(
    dayjs
      .min([
        appointmentStart.add(appointment.timeOffStart, "minutes"),
        locationEnd,
      ])
      .diff(appointmentStartInDay, "minutes"),
    0,
  );

  const timeoffDuration = appointment.timeOffDuration
    ? dayjs
        .min([
          appointmentStart
            .add(appointment.timeOffStart, "minutes")
            .add(appointment.timeOffDuration, "minutes"),
          locationEnd,
        ])
        .diff(appointmentStartInDay.add(startDuration, "minutes"), "minutes")
    : 0;

  const timeoffEndDuration = appointment.timeOffDuration
    ? dayjs
        .min([
          appointmentStart
            .add(appointment.timeOffStart, "minutes")
            .add(appointment.timeOffDuration, "minutes")
            .add(appointment.timeOffEnd, "minutes"),
          locationEnd,
        ])
        .diff(
          appointmentStartInDay
            .add(timeoffDuration, "minutes")
            .add(startDuration, "minutes"),
          "minutes",
        )
    : 0;

  const upperBlockHeight =
    transform && isAppointmentOverMultipleDays
      ? 100
      : returnHeightOfEventBlock({
          duration: startDuration,
          slotHeight: calendarData.slotHeight,
          timeStep: calendarData.timeStep,
        });

  const shouldDisplayServiceName = upperBlockHeight > 50;
  const shouldDisplayTime = upperBlockHeight > 30;
  const timeoffHeight =
    transform && isAppointmentOverMultipleDays
      ? 0
      : returnHeightOfEventBlock({
          duration: timeoffDuration,
          slotHeight: calendarData.slotHeight,
          timeStep: calendarData.timeStep,
        });

  const lowerBlockHeight =
    transform && isAppointmentOverMultipleDays
      ? 0
      : returnHeightOfEventBlock({
          duration: timeoffEndDuration,
          slotHeight: calendarData.slotHeight,
          timeStep: calendarData.timeStep,
        });

  const totalHeight =
    transform && isAppointmentOverMultipleDays
      ? 100
      : returnHeightOfEventBlock({
          duration: calculatedDuration,
          slotHeight: calendarData.slotHeight,
          timeStep: calendarData.timeStep,
        });

  const blockBeforeHeight = returnHeightOfEventBlock({
    duration: appointment.blockBeforeMins,
    timeStep: calendarData.timeStep,
    slotHeight: calendarData.slotHeight,
  });

  const blockAfterHeight = returnHeightOfEventBlock({
    duration: appointment.blockAfterMins,
    timeStep: calendarData.timeStep,
    slotHeight: calendarData.slotHeight,
  });

  let upperContent;
  let lowerContent = null;

  const isTimeoffBlockingRelevant =
    isTimeoffBlockingResource && calendarMode === "resources";

  const heightTotal = totalHeight;
  const topBlockHeight = isTimeoffBlockingRelevant
    ? heightTotal
    : upperBlockHeight;
  const bottomBlockHeight = isTimeoffBlockingRelevant ? null : lowerBlockHeight;
  const bottomYOffset = isTimeoffBlockingRelevant ? null : timeoffHeight;

  const startDragTime = dayjs(`${appointmentDate}T${dragTime}`);
  const isDragTimeValid = startDragTime.isValid();
  const endDragTime = startDragTime.add(appointment.duration, "minutes");

  const { locale } = i18n;
  const startDragDateTimeFormatted = isAppointmentOverMultipleDays
    ? startDragTime.toDate().toLocaleDateString(locale, {
        hour: "2-digit",
        minute: "2-digit",
        day: "2-digit",
        month: "2-digit",
      })
    : "";

  const endDragDateTimeFormatted = isAppointmentOverMultipleDays
    ? endDragTime.toDate().toLocaleDateString(locale, {
        hour: "2-digit",
        minute: "2-digit",
        day: "2-digit",
        month: "2-digit",
      })
    : "";

  const startDragTimeFormatted = isAppointmentOverMultipleDays
    ? ""
    : startDragTime.toDate().toLocaleTimeString(locale, {
        hour: "2-digit",
        minute: "2-digit",
      });

  const draggingContent = (
    <div className="relative z-999999 flex h-full items-start justify-start overflow-visible p-2 text-xs font-medium md:items-center md:justify-center md:p-0">
      <div>
        {isAppointmentOverMultipleDays
          ? isDragTimeValid && (
              <>
                <span className="absolute -top-20 left-1/2 w-full max-w-96 -translate-x-1/2 rounded-xl bg-gray-200 px-2 py-1 outline-1 outline-solid">
                  {startDragDateTimeFormatted} - {endDragDateTimeFormatted}
                </span>

                <div className="flex flex-col items-center gap-0">
                  <span>{startDragDateTimeFormatted}</span>
                  <span>-</span>
                  <span>{endDragDateTimeFormatted}</span>
                </div>
              </>
            )
          : isDragTimeValid && (
              <span className="absolute -top-12 left-1/2 -translate-x-1/2 rounded-xl bg-gray-200 px-2 py-1 outline-1 outline-solid">
                {startDragTimeFormatted}
              </span>
            )}
      </div>
    </div>
  );

  if (transform) {
    upperContent = draggingContent;
  } else if (isPreScheduled) {
    upperContent = (
      <PreScheduledAppointmentContent
        appointment={appointment}
        view={calendarView}
        color={appointment ? serviceColor : "grey"}
        shown={isShown}
        showUsers={permission}
        calendarMode={calendarMode}
      />
    );
  } else {
    const { upperBlockContent, lowerBlockContent } =
      SingleUserAppointmentContent({
        shouldDisplayTime,
        shouldDisplayServiceName,
        onClickAppointment,
        appointment,
        view: calendarView,
        color: appointment ? serviceColor : "grey",
        shown: isShown,
        showUsers: permission,
        calendarMode,
        topBlockHeight,
        hideIsReceiptIssuedIconOnAppointment,
        isStaffRequestIndicatorVisible,
      });
    upperContent = upperBlockContent;
    lowerContent = lowerBlockContent;
  }

  const dateTime = appointment.date + "T" + startTime;
  const isInPast = Date.parse(dateTime) < Date.now();

  if (service == null) {
    return null;
  }

  let cursor = "pointer";
  if (transform) cursor = "move";
  if (!permission) cursor = "default";

  return (
    <>
      {blockBeforeMins > 0 && (
        <BlockTime
          xOffset={`${xOffset}%`}
          transformStyle={
            transform
              ? `translate3d(${transform.x}px, ${transform.y + lastClickYOffset}px, 0)`
              : undefined
          }
          top={yOffset - blockBeforeHeight}
          height={blockBeforeHeight}
          width={`${width}%`}
          serviceColor={serviceColor}
        />
      )}
      <div
        role="presentation"
        onMouseDown={(event) => {
          if (!isAppointmentOverMultipleDays) return;
          const parentElement = event.target.parentElement;
          const parentRect = parentElement.getBoundingClientRect();
          const offsetY = event.clientY - parentRect.top;
          setLastClickYOffset(offsetY);
        }}
      >
        <Tooltip
          showArrow
          placement={calendarView === "day" ? "bottom" : "left"}
          classNames={{
            base: "pointer-events-none",
          }}
          closeDelay={0}
          isOpen={popoverOpen && !transform && window.innerWidth >= 768}
          content={
            <HoverContent
              resources={resources}
              users={users}
              appointment={appointment}
              viewCustomersPermission={permission}
            />
          }
        >
          <BlockWrapper
            type="button"
            data-appointment-id={appointmentId}
            hasTimeOff={appointment.timeOffDuration > 0}
            overlapIndex={overlapIndex}
            width={width}
            hoverWidth={hoverWidth}
            zIndex={zIndex}
            xOffset={xOffset}
            ref={setNodeRef}
            style={style}
            height={heightTotal}
            lowerBlockHeight={bottomBlockHeight}
            lowerYoffset={bottomYOffset}
            upperBlockHeight={topBlockHeight}
            yOffset={yOffset}
            color={serviceColor}
            isShown={viewMode === "appointments" && isShown}
            cursor={cursor}
            isPreScheduled={isPreScheduled}
            isPreScheduledFull={
              isPreScheduled &&
              appointment.customers.length >= appointment.numberOfPeople
            }
            isInPast={isInPast}
            className={cn("aria-expanded:opacity-100", {
              hovered: popoverOpen,
            })}
          >
            {appointment.customers.length === 1 &&
              appointment.customers[0].customerDidAttend === -1 && (
                <NoShowOverlay>
                  <p className="text-xs leading-4 font-semibold -tracking-[0.015rem] text-black uppercase">
                    <Trans>Neprihod</Trans>
                  </p>
                </NoShowOverlay>
              )}

            <UpperBlock
              role={"presentation"}
              className={"block-part"}
              onMouseEnter={() => setPopoverOpen(true)}
              onMouseLeave={() => setPopoverOpen(false)}
              onClick={(e) => {
                if (wasDragging) {
                  return;
                }
                e.stopPropagation();
                if (!permission) return;
                setHideModalTabs(true);
                setSelectedAppointmentId(appointmentId);
                setPopoverOpen(false);
              }}
              {...listeners}
              {...attributes}
              height={topBlockHeight}
              zIndex={zIndex}
              color={serviceColor}
              isShown={viewMode === "appointments" && isShown}
              isPreScheduled={isPreScheduled}
              isDragging={transform != null}
            >
              {upperContent}
            </UpperBlock>
            <div
              style={{ height: `${bottomYOffset}px` }}
              className="time-off-block"
            />
            {bottomBlockHeight > 0 && (
              <LowerBlock
                role="presentation"
                className={"block-part"}
                onMouseEnter={() => setPopoverOpen(true)}
                onMouseLeave={() => setPopoverOpen(false)}
                onClick={(e) => {
                  if (wasDragging) {
                    return;
                  }
                  e.stopPropagation();
                  if (!permission) return;
                  setHideModalTabs(true);
                  setSelectedAppointmentId(appointmentId);
                  setPopoverOpen(false);
                }}
                {...listeners}
                {...attributes}
                height={bottomBlockHeight}
                zIndex={zIndex}
                color={serviceColor}
                isShown={viewMode === "appointments" && isShown}
                isPreScheduled={isPreScheduled}
              >
                {lowerContent}
              </LowerBlock>
            )}
          </BlockWrapper>
        </Tooltip>
      </div>

      {blockAfterHeight > 0 && (
        <BlockTime
          xOffset={`${xOffset}%`}
          transformStyle={
            transform
              ? `translate3d(${transform.x}px, ${transform.y + lastClickYOffset}px, 0)`
              : undefined
          }
          top={yOffset + heightTotal}
          height={blockAfterHeight}
          width={`${width}%`}
          serviceColor={serviceColor}
        />
      )}
    </>
  );
};

const UserResourceAvatar = ({ appointment, calendarMode }) => {
  const { resources, users } = useCalendarStore.getState();

  // Disabled resources stay on the appointment but aren't loaded into the store.
  const foundItems =
    calendarMode === "users"
      ? (appointment.resources ?? [])
          .map((id) => resources.find((r) => r.id === id))
          .filter(Boolean)
      : (appointment.userIds ?? [])
          .map((id) => users.find((u) => u.userId === id))
          .filter(Boolean);

  if (foundItems.length === 0) {
    return null;
  }

  return (
    <div className="absolute right-1 bottom-1 z-1000">
      <div className="flex items-center gap-px">
        {foundItems.map((foundItem, index) => {
          const initials =
            calendarMode === "users"
              ? getInitials(foundItem?.label)
              : getInitials(`${foundItem?.name} ${foundItem?.lastName || ""}`);

          return (
            <div
              key={index}
              className="relative -ml-1.5 flex h-5 w-5 items-center justify-center overflow-hidden rounded-full bg-[#EBF8EF] text-[10px] font-medium text-gray-700 first:ml-0"
            >
              {foundItem?.fullImageUrl || foundItem?.imageUrl ? (
                <img
                  src={foundItem.fullImageUrl ?? foundItem.imageUrl}
                  alt={`${foundItem.name} ${foundItem.lastName || ""}`}
                  className="h-full w-full object-cover"
                />
              ) : (
                <span className="text-[10px]">{initials}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const SingleUserAppointmentContent = ({
  onClickAppointment,
  appointment,
  view,
  color,
  shown,
  showUsers,
  calendarMode,
  shouldDisplayServiceName,
  topBlockHeight,
  shouldDisplayTime,
  hideIsReceiptIssuedIconOnAppointment,
  isStaffRequestIndicatorVisible,
}) => {
  const customer = appointment?.customers?.at(0);
  const { startTime, endTime } = appointment.formattedData;

  // Aggregate subscription warnings across all customers on this appointment
  const hasAnySubscriptionWarnings = (appointment.customers || []).some((c) => {
    const warnings = c?.customerSubscriptionWarnings;
    return warnings?.hasUnpaidInvoices || warnings?.isExpiringSoon;
  });

  // A marketplace booking also carries `bookedFromForm`, so it takes priority
  // to avoid stacking two "booked online" icons on the same block.
  const bookingIcon = customer?.bookedFromMarketplace ? (
    <MarketplaceBookingIndicator size={13} />
  ) : customer?.bookedFromForm ? (
    <FiGlobe size=".8rem" />
  ) : null;

  const addonIcon =
    customer?.serviceAddons?.length > 0 ? <HeartPlus size=".8rem" /> : null;

  const requestedStaffIcon =
    isStaffRequestIndicatorVisible && customer?.staffRequested ? (
      <Heart size=".8rem" fill="currentColor" />
    ) : null;

  const paymentIcon = customer?.paymentStatus ? (
    customer.paymentStatus === "succeeded" ? (
      <BsCurrencyEuro size=".8rem" />
    ) : (
      getStatusIcon(
        customer.paymentStatus === "succeeded" ? "success" : "warning",
      )
    )
  ) : null;

  const receiptIcon =
    !hideIsReceiptIssuedIconOnAppointment && customer?.invoiceId != null ? (
      <ReceiptText size=".8rem" />
    ) : null;

  const subscriptionUsage = customer?.customerSubscriptionUsageAllTimeframe;
  const subscriptionUsageLabel = subscriptionUsage
    ? `${subscriptionUsage.usedAmount}/${subscriptionUsage.limitAmount}`
    : null;

  const fullTimeRange = `${startTime} - ${endTime}`;

  let upperTimeRange = fullTimeRange;
  let lowerTimeRange = "";
  if (appointment.timeOffStart && appointment.timeOffDuration) {
    const serviceStartTime = startTime;
    const timeOffStartTime = dayjs(serviceStartTime, "HH:mm")
      .add(appointment.timeOffStart, "minute")
      .format("HH:mm");
    const timeOffEndTime = dayjs(serviceStartTime, "HH:mm")
      .add(appointment.timeOffStart + appointment.timeOffDuration, "minute")
      .format("HH:mm");
    const serviceEndTime = endTime;
    upperTimeRange = `${serviceStartTime} - ${timeOffStartTime}`;
    lowerTimeRange = `${timeOffEndTime} - ${serviceEndTime}`;
  }

  const lowerBlockTimeDisplay =
    appointment.timeOffStart && appointment.timeOffDuration
      ? lowerTimeRange
      : "";

  const upperBlockContent = (
    <>
      <AppointmentText
        view={view}
        color={appointment ? appointment.serviceColor : color}
      >
        {shown && appointment && (
          <>
            {appointment.hideData && (
              <span className="truncate text-[10px] leading-px text-[#262626]">
                {shouldDisplayServiceName && appointment.serviceName}{" "}
              </span>
            )}
            <div className="flex w-full items-start pt-px">
              {!appointment.hideData && showUsers && (
                <div className="leading-none">
                  <span
                    className={`text-[12px] leading-px font-semibold text-[#262626] ${topBlockHeight <= 60 ? "truncate" : ""}`}
                  >
                    {`${customer?.name} ${customer?.lastName || ""}`}
                  </span>
                </div>
              )}
            </div>
            {shouldDisplayTime && (
              <span className="flex w-full items-center gap-1 text-left text-[10px] whitespace-nowrap">
                {paymentIcon && <span>{paymentIcon}</span>}
                {bookingIcon && <span>{bookingIcon}</span>}
                {addonIcon && <span>{addonIcon}</span>}
                {receiptIcon && <span>{receiptIcon}</span>}
                {requestedStaffIcon && (
                  <span
                    role="img"
                    aria-label={i18n._(
                      "Stranka je želela točno tega izvajalca",
                    )}
                    title={i18n._("Stranka je želela točno tega izvajalca")}
                  >
                    {requestedStaffIcon}
                  </span>
                )}
                {hasAnySubscriptionWarnings && (
                  <span
                    title={i18n._("Opozorilo za paket")}
                    className="text-amber-600"
                  >
                    <CircleAlert size={12} />
                  </span>
                )}
                {subscriptionUsageLabel && (
                  <span className="rounded-sm bg-white/70 px-1 text-[9px] font-semibold text-[#262626]">
                    {subscriptionUsageLabel}
                  </span>
                )}
                {upperTimeRange}
              </span>
            )}

            {!lowerBlockTimeDisplay && (
              <>
                <span className="truncate text-[10px] text-[#262626]">
                  {shouldDisplayServiceName && appointment.serviceName}{" "}
                </span>

                <p
                  className="block w-full px-[2px] py-0 text-left text-[10px] font-medium text-[#262626]"
                  style={{
                    backgroundColor: `${appointment.serviceColor}50`,
                  }}
                >
                  {customer?.comment}
                </p>
              </>
            )}
          </>
        )}
      </AppointmentText>
      <UserResourceAvatar
        appointment={appointment}
        calendarMode={calendarMode}
      />
    </>
  );

  const lowerBlockContent = (
    <AppointmentText>
      {shown && appointment && (
        <>
          {lowerBlockTimeDisplay && shouldDisplayTime && (
            <span className="truncate text-[10px] text-[#424b64]">
              {lowerBlockTimeDisplay} {appointment.serviceName}
            </span>
          )}
        </>
      )}
    </AppointmentText>
  );

  return { upperBlockContent, lowerBlockContent };
};

const PreScheduledAppointmentContent = ({
  appointment,
  view,
  color,
  shown,
  showUsers,
  calendarMode,
}) => {
  const { startTime, endTime } = appointment.formattedData;
  const hasAnySubscriptionWarnings = (appointment.customers || []).some((c) => {
    const warnings = c?.customerSubscriptionWarnings;
    return warnings?.hasUnpaidInvoices || warnings?.isExpiringSoon;
  });
  return (
    <>
      <AppointmentText
        view={view}
        color={appointment ? appointment.serviceColor : color}
      >
        {shown && appointment && (
          <div className="flex h-full flex-col">
            <div>
              <div className="flex items-center gap-1">
                <p className="text-[11px] font-semibold">
                  {appointment.customers.length}/{appointment.numberOfPeople}
                </p>
                <span
                  className={
                    "flex w-full items-center gap-1 text-left text-[10px] whitespace-nowrap"
                  }
                  style={{ width: "fit-content" }}
                >
                  {hasAnySubscriptionWarnings && (
                    <span
                      title={i18n._("Opozorilo za paket")}
                      className="text-amber-600"
                    >
                      <CircleAlert size={12} />
                    </span>
                  )}
                  {`${startTime} - ${endTime}`}
                </span>
              </div>
              <div>
                <p className="text-left text-[10px] leading-3 font-semibold text-[#797878]">
                  {appointment.serviceName}
                </p>
              </div>

              {!appointment.hideData && showUsers && (
                <div className="flex w-full flex-col items-start justify-start">
                  {appointment.customers.map((customer, customerIndex) => {
                    const paymentIcon = customer?.paymentStatus ? (
                      customer.paymentStatus === "succeeded" ? (
                        <BsCurrencyEuro size=".8rem" />
                      ) : (
                        getStatusIcon(
                          customer.paymentStatus === "succeeded"
                            ? "success"
                            : "warning",
                        )
                      )
                    ) : null;

                    const bookingIcon = customer?.bookedFromMarketplace ? (
                      <MarketplaceBookingIndicator size={11} />
                    ) : customer?.bookedFromForm ? (
                      <FiGlobe size=".7rem" />
                    ) : null;
                    return (
                      <div key={customerIndex} className="flex items-center">
                        <span className="text-[10px] leading-3 font-semibold whitespace-nowrap text-[#262626]">
                          {`${customer?.name} ${customer?.lastName}`}{" "}
                        </span>
                        {(paymentIcon != null || bookingIcon != null) && (
                          <div className="ml-1 flex h-[18px] items-center justify-center rounded-full text-[0.7rem]">
                            {paymentIcon} {bookingIcon}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </AppointmentText>
      <UserResourceAvatar
        appointment={appointment}
        calendarMode={calendarMode}
      />
    </>
  );
};

const AppointmentText = styled.div`
  overflow: hidden;
  color: #424b64;
  display: flex;
  align-items: flex-start;
  flex-direction: column;
  gap: 0;
  font-size: 12px;
  width: 100%;
  z-index: 3;
  position: relative;
  ${(props) =>
    props.view === "month" &&
    "display: -webkit-box;\n  -webkit-box-orient: vertical;\n    -webkit-line-clamp: 1;"};
  @media (max-width: 768px) {
    ${(props) => props.view === "month" && "font-size: 8px;"}
  }
`;

const UpperBlock = styled.div`
  z-index: ${(props) => props.zIndex};
  background: white;
  position: relative;
  height: ${(props) => `${props.height}px`};
  padding-left: 1px;
  border-radius: 4px;
  text-align: left;
  max-width: 100%;
  overflow: ${(props) => (props.isDragging ? "visible" : "hidden")};
  padding: ${(props) => (props.height > 10 ? "5px" : "0px")};
  transform-style: preserve-3d;
  backface-visibility: hidden;
  width: 100%;
  transition: all 0.15s ease-in-out;
  pointer-events: auto;

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 4px;
    background: ${(props) => (props.isShown ? `${props.color}90` : "#bbbbbb")};
    ${({ isPreScheduled, color }) =>
      isPreScheduled &&
      `
        background: ${color}80;
      `}
    z-index: 1;
    transform: translateZ(0);
    transition: inherit;
  }
`;

const LowerBlock = styled.div`
  z-index: ${(props) => props.zIndex};
  background: white;
  position: relative;
  height: ${(props) =>
    props.height && props.height > 0 ? `${props.height}px` : 0};
  padding-left: 1px;
  border-radius: 4px;
  padding: clamp(
    0px,
    5px,
    ${(props) =>
      `clamp(0px, 5px, ${props.isShown && props.height > 10 ? "1%" : 0})`}
  );
  transform-style: preserve-3d;
  backface-visibility: hidden;
  width: 100%;
  transition:
    all 0.15s ease-in-out,
    transform 0s;
  pointer-events: auto;

  &:before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 4px;
    background: ${(props) => (props.isShown ? `${props.color}90` : "#bbbbbb")};
    ${({ isPreScheduled, color }) =>
      isPreScheduled &&
      `
        background: ${color}80;
      `}
    z-index: 1;
    transform: translateZ(0);
    transition: inherit;
  }
`;

const BlockWrapper = styled.button`
  position: absolute;
  outline: 1px solid white;
  width: ${(props) => props.width}%;
  height: ${(props) => `${props.height}px`};
  top: ${(props) => `${props.yOffset}px`};
  left: ${(props) => `${props.xOffset}%`};
  overflow: visible;
  transform-style: preserve-3d;
  backface-visibility: hidden;
  transform-origin: top left;
  transition:
    all 0.15s ease-in-out,
    transform 0s;

  z-index: ${(props) => props.zIndex};
  border-radius: 4px;
  pointer-events: none;
  cursor: ${(props) => props.cursor};
  user-select: none;
  ${(props) => (!props.isShown ? "opacity: 0.3; border-radius: 0;" : "")}

  ${({ isPreScheduled, color, isPreScheduledFull }) =>
    isPreScheduled &&
    `
      outline: 2px solid ${color};
      outline-width: 2px 2px 2px 2px;
      outline-style: ${isPreScheduledFull ? "solid" : "dashed"};
      outline-radius: 4px;
    `};

  .time-off-block {
    pointer-events: none;
  }

  &:after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: ${(props) => props.zIndex + 999999};
    background: #e7e7e8;
    opacity: ${(props) => (props.isInPast ? "0.4" : "0")};
    border-radius: 6px;
    pointer-events: none;
  }

  border-left: ${(props) =>
    props.hasTimeOff ? `3px solid ${props.color}` : "none"};

  &.hovered {
    width: ${(props) => props.hoverWidth}%;
    left: 0;
    outline: 1px solid black;
    z-index: ${(props) => props.zIndex + 10000};
  }
`;

const NoShowOverlay = ({ children }) => (
  <div className="absolute top-0 left-0 z-99999 flex h-full w-full items-center justify-center rounded-sm bg-black/40">
    {children}
  </div>
);

const BlockTime = ({
  top,
  height,
  width,
  serviceColor,
  transformStyle,
  xOffset,
}) => {
  return (
    <div
      style={{
        top,
        left: xOffset,
        height,
        width,
        transform: transformStyle,
        borderRadius: "4px",
        backgroundImage: `repeating-linear-gradient(159.5deg, transparent, transparent 8px, ${serviceColor}30 8px, ${serviceColor}30 18px)`,
      }}
      className="absolute"
    />
  );
};

export default React.memo(AppointmentBlock);
